import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowRight, ChevronRight, CheckCircle2, Phone } from 'lucide-react';
import SEO from '@/components/SEO';
import { Reveal } from '@/lib/useReveal';
import { getServiceBySlug, SERVICES, SERVICE_DEFAULTS } from '@/lib/services';
import { CONTACT_INFO } from '@/lib/content';

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const service = getServiceBySlug(slug);
  if (!service) return <Navigate to="/services" replace />;

  const process = service.process || SERVICE_DEFAULTS.PROCESS_DEFAULT;
  const stats = service.extraStats || SERVICE_DEFAULTS.STAT_BAR;
  const related = SERVICES.filter((s) => s.slug !== service.slug && s.category === service.category).slice(0, 3);
  const relatedFinal = related.length >= 3 ? related : [...related, ...SERVICES.filter((s) => s.slug !== service.slug && !related.includes(s)).slice(0, 3 - related.length)];

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: service.name,
    serviceType: service.name,
    provider: { '@type': 'Organization', name: 'Brand Partners', url: 'https://bp.agency' },
    description: service.hero,
    areaServed: 'United States',
  };

  return (
    <main data-testid={`service-detail-${service.slug}`} className="bg-white">
      <SEO
        title={`${service.name} | Brand Partners`}
        description={service.short}
        path={`/services/${service.slug}`}
        schema={schema}
      />

      {/* HERO */}
      <section className="relative pt-10 lg:pt-16 pb-14 lg:pb-20 bg-radial-blue overflow-hidden">
        <div className="absolute inset-0 bg-dot-grid opacity-40 pointer-events-none" />
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8 relative">
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-[13px] text-bp-slate-soft mb-6">
            <Link to="/services" className="hover:text-bp-blue flex items-center gap-1.5">
              <ArrowRight size={13} className="rotate-180"/> All Services
            </Link>
            <ChevronRight size={12} />
            <span className="text-bp-slate font-medium">{service.name}</span>
          </nav>

          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              {service.featured && (
                <Reveal as="div" className="inline-flex items-center gap-2 bg-white border border-bp-amber/30 rounded-full px-3 py-1 shadow-sm mb-5">
                  <span className="w-1.5 h-1.5 rounded-full bg-bp-amber"/>
                  <span className="text-[12px] font-semibold text-bp-amber uppercase tracking-wider">Most Popular Service</span>
                </Reveal>
              )}
              <Reveal as="div" className="kicker"><span className="dot"/>{service.name}</Reveal>
              <Reveal as="h1" delay={80} className="h-display mt-4 text-[42px] sm:text-[60px] lg:text-[72px]">
                {service.name.split(/[()]/).filter(Boolean).map((part, i) => (
                  i === 0 ? part.trim() : <span key={i} className="grad-text"> ({part.trim()})</span>
                ))}
              </Reveal>
              <Reveal as="p" delay={140} className="mt-6 text-[17px] leading-relaxed text-bp-slate max-w-2xl">
                {service.hero}
              </Reveal>
              <Reveal as="div" delay={200} className="mt-8 flex flex-wrap gap-3">
                <Link to="/contact" className="btn-primary" data-testid="service-cta-primary">
                  <Phone size={15}/> Request A Free Estimate
                </Link>
                <Link to="/contact" className="btn-secondary" data-testid="service-cta-secondary">
                  Contact Us <ArrowRight size={15}/>
                </Link>
              </Reveal>
            </div>

            <Reveal as="div" delay={150} className="lg:col-span-5">
              <div className="relative">
                <img
                  src={service.image}
                  alt={`${service.name} - Brand Partners`}
                  className="w-full h-[380px] object-cover rounded-3xl shadow-xl"
                  loading="eager"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* WHAT WE OFFER */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <Reveal as="h2" className="h-display text-[36px] sm:text-[48px]">
              What we <span className="grad-text">offer</span>
            </Reveal>
            <Reveal as="p" delay={80} className="mt-4 text-[17px] text-bp-slate">
              Our {service.name.toLowerCase()} services are comprehensive and tailored to your business goals.
            </Reveal>
          </div>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {service.offerings.map((o, i) => (
              <Reveal as="article" key={o.t} delay={i * 60} className="card-soft p-7">
                <div className="w-10 h-10 rounded-xl bg-bp-blue-50 text-bp-blue flex items-center justify-center font-display font-bold text-[15px]">
                  {String(i + 1).padStart(2, '0')}
                </div>
                <h3 className="font-display font-bold text-[19px] text-bp-ink mt-5 leading-snug">{o.t}</h3>
                <p className="mt-2.5 text-[14.5px] leading-relaxed text-bp-slate">{o.b}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE BP */}
      <section className="py-20 lg:py-24 bg-bp-slate-50">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <Reveal as="div" className="kicker"><span className="dot"/>Why Brand Partners</Reveal>
            <Reveal as="h2" delay={80} className="h-display mt-3 text-[36px] sm:text-[48px]">
              Why choose us for <span className="grad-text">{service.name.split('(')[0].trim()}?</span>
            </Reveal>
            <Reveal as="ul" delay={140} className="mt-8 space-y-3.5">
              {service.benefits.map((b) => (
                <li key={b} className="flex items-start gap-3">
                  <CheckCircle2 size={20} className="text-bp-teal flex-shrink-0 mt-0.5"/>
                  <span className="text-[15.5px] text-bp-slate leading-relaxed">{b}</span>
                </li>
              ))}
            </Reveal>
            <Reveal as="div" delay={200} className="mt-8">
              <Link to="/contact" className="btn-primary">Request A Free Estimate <ArrowRight size={15}/></Link>
            </Reveal>
          </div>

          <Reveal as="div" delay={120} className="lg:col-span-6 grid grid-cols-2 gap-4">
            {stats.map((s) => (
              <div key={s.l} className="card-floating p-6 text-center">
                <div className="font-display font-extrabold text-[44px] leading-none grad-text">{s.v}</div>
                <div className="text-[12px] font-semibold uppercase tracking-wider text-bp-slate-soft mt-3">{s.l}</div>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* OUR PROCESS */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <Reveal as="div" className="kicker justify-center"><span className="dot"/>Our Process</Reveal>
            <Reveal as="h2" delay={80} className="h-display mt-3 text-[36px] sm:text-[48px]">
              A proven process. <span className="grad-text">Real results.</span>
            </Reveal>
            <Reveal as="p" delay={140} className="mt-4 text-[17px] text-bp-slate">
              A transparent, repeatable process that delivers results - refined over 19 years.
            </Reveal>
          </div>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
            {process.map((p, i) => (
              <Reveal as="div" key={p.n} delay={i * 80} className="card-soft p-6 lg:p-7 relative">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-bp-blue to-bp-teal text-white flex items-center justify-center font-display font-bold text-lg">
                  {p.n}
                </div>
                <h3 className="font-display font-bold text-[19px] text-bp-ink mt-5">{p.title}</h3>
                <p className="text-[14px] text-bp-slate mt-2.5 leading-relaxed">{p.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* RELATED */}
      <section className="py-20 lg:py-24 bg-bp-slate-50">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <Reveal as="div" className="kicker justify-center"><span className="dot"/>Related Services</Reveal>
            <Reveal as="h2" delay={80} className="h-display mt-3 text-[36px] sm:text-[48px]">
              Amplify your <span className="grad-text">results.</span>
            </Reveal>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-5">
            {relatedFinal.map((r, i) => (
              <Reveal as="article" key={r.slug} delay={i * 80}>
                <Link to={`/services/${r.slug}`} className="card-soft p-7 flex flex-col h-full group">
                  <h3 className="font-display font-bold text-[18px] text-bp-ink group-hover:text-bp-blue transition-colors">{r.name}</h3>
                  <p className="text-[14px] text-bp-slate mt-2 leading-relaxed flex-1">{r.short}</p>
                  <span className="inline-flex items-center gap-1.5 mt-5 text-[13px] font-semibold text-bp-blue">
                    Learn More <ArrowRight size={13}/>
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-8">
          <div className="rounded-3xl p-10 lg:p-14 text-center text-white shadow-2xl" style={{background: 'linear-gradient(135deg, #0072EE 0%, #14B8A6 100%)'}}>
            <h2 className="font-display font-extrabold text-[32px] sm:text-[44px] leading-[1.05] text-white">
              Ready to get started with {service.name}?
            </h2>
            <p className="mt-5 text-[17px] text-white/90 max-w-xl mx-auto">
              Get a free, no-obligation estimate tailored to your business goals. Our team is ready to help you grow.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-white text-bp-blue font-bold shadow-lg hover:bg-bp-blue-50 transition-colors">
                <Phone size={15}/> Request A Free Estimate
              </Link>
              <a href={`tel:${CONTACT_INFO.phoneTel}`} className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-white/15 text-white font-semibold hover:bg-white/25 transition-colors">
                <Phone size={15}/> {CONTACT_INFO.phone}
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
